import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Alert, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import useUpsertStatementConfig from '@/app/(dashboard)/master/statement/apis/use-upsert-statement-config';
import type { StatementConfigDTO } from '@/app/(dashboard)/master/statement/apis/use-get-statement-configs';
import { fetchMyStatementConfigs } from '@/features/statement-configs/api';
import { expectedUpdatedAtFor, pickConflictFallback } from '@/features/statement-configs/conflict';
import { StatementConflictPanel } from '@/features/statement-configs/StatementConflictPanel';
import { resolveFirstLoginCopy } from '../i18n';

export interface StepPernyataanHandle {
  /** Saves through the per-PPJK upsert. Resolves false when nothing was saved. */
  save: () => Promise<boolean>;
  /** All three fields filled. */
  isComplete: () => boolean;
  /** Differs from what the last successful save sent. */
  isDirty: () => boolean;
  /**
   * Continue on top of a colleague's Utama ("Tetap ubah"): fills the fields
   * with it and makes the next save expect its updatedAt.
   */
  adoptCurrent: (config: AdoptableStatement) => void;
}

interface StepPernyataanProps {
  /**
   * Reports whether the form differs from its last successful save, after
   * every edit and after every save. State, not a ref read: the host renders
   * before this component's handle is refreshed for the same keystroke.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /** Every save's lifecycle, so the host can tell its own broadcast from a colleague's. */
  onSaveStateChange?: (state: 'start' | 'success' | 'error') => void;
  /** After any successful save, the host's button or this step's "Timpa". */
  onSaved?: () => void;
  /** The save-time conflict panel opened or closed. */
  onConflictChange?: (open: boolean) => void;
}

/** The fields a colleague's row needs for "Tetap ubah" and the conflict panel's reload. */
export type AdoptableStatement = Pick<StatementConfigDTO, 'place' | 'name' | 'position'> &
  Partial<Pick<StatementConfigDTO, 'updatedAt'>>;

/** Step 1: the PPJK's signature block. Uppercased on blur like the PIB form. */
const StepPernyataan = forwardRef<StepPernyataanHandle, StepPernyataanProps>((props, ref) => {
  const { onDirtyChange, onSaveStateChange, onSaved, onConflictChange } = props;
  const { colors: themeColors } = useTheme();
  const { language } = useLanguage();
  const t = (key: string) => resolveFirstLoginCopy(language, key);
  const { upsertConfig, loading } = useUpsertStatementConfig();

  const [place, setPlace] = useState('');
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [error, setError] = useState<string | null>(null);
  const lastSaved = useRef<{ place: string; name: string; position: string } | null>(null);
  // The Utama this form builds on. Null until a save or an adopt: the first
  // save then tells the server it expects no Utama yet.
  const baseRow = useRef<AdoptableStatement | null>(null);
  // A colleague saved first: their row (null while it is being refetched, or
  // when that read failed).
  const [conflict, setConflict] = useState<{ current: StatementConfigDTO | null; loadFailed?: boolean } | null>(null);
  const conflictOpen = conflict !== null;
  useEffect(() => {
    onConflictChange?.(conflictOpen);
  }, [conflictOpen, onConflictChange]);

  const normalised = () => ({
    place: place.trim().toUpperCase(),
    name: name.trim().toUpperCase(),
    position: position.trim().toUpperCase(),
  });
  const isComplete = () => place.trim() !== '' && name.trim() !== '' && position.trim() !== '';
  const isDirty = () => {
    const saved = lastSaved.current;
    if (!saved) return true;
    const now = normalised();
    return now.place !== saved.place || now.name !== saved.name || now.position !== saved.position;
  };

  useEffect(() => {
    onDirtyChange?.(isDirty());
  }, [place, name, position, onDirtyChange]);

  const adoptCurrent = (config: AdoptableStatement) => {
    const values = {
      place: (config.place ?? '').trim().toUpperCase(),
      name: (config.name ?? '').trim().toUpperCase(),
      position: (config.position ?? '').trim().toUpperCase(),
    };
    setPlace(values.place);
    setName(values.name);
    setPosition(values.position);
    baseRow.current = config;
    lastSaved.current = values;
    setConflict(null);
    setError(null);
    onDirtyChange?.(false);
  };

  /** A 409 without the colleague's row (older backend): read the Utama. */
  const loadConflictRow = async () => {
    setConflict({ current: null });
    try {
      const current = pickConflictFallback(await fetchMyStatementConfigs());
      setConflict(current ? { current } : { current: null, loadFailed: true });
    } catch {
      setConflict({ current: null, loadFailed: true });
    }
  };

  /** `expectedOverride` is the colleague's updatedAt when the user chose "Timpa". */
  const save = async (expectedOverride?: string | null) => {
    if (!isComplete()) {
      setError(t('pernyataan.required'));
      return false;
    }
    setError(null);
    const payload = normalised();
    onSaveStateChange?.('start');
    const { saved, conflict: lost } = await upsertConfig({
      ...payload,
      expectedUpdatedAt: expectedOverride !== undefined ? expectedOverride : expectedUpdatedAtFor(baseRow.current),
    });
    onSaveStateChange?.(saved ? 'success' : 'error');
    if (lost) {
      if (lost.current) setConflict({ current: lost.current });
      else await loadConflictRow();
      return false;
    }
    if (!saved) {
      setError(t('pernyataan.saveFailed'));
      return false;
    }
    baseRow.current = saved;
    lastSaved.current = payload;
    setConflict(null);
    onDirtyChange?.(false);
    onSaved?.();
    return true;
  };

  useImperativeHandle(ref, () => ({
    isComplete,
    isDirty,
    adoptCurrent,
    save: () => save(),
  }));

  const inputStyles = {
    label: { color: themeColors.text, fontWeight: 500 },
    input: {
      backgroundColor: themeColors.formFields,
      color: themeColors.text,
      borderColor: themeColors.border,
    },
  };

  return (
    <Stack gap="md">
      <div>
        <Text component="h3" fw={700} style={{ color: themeColors.text, fontSize: 22, margin: 0, lineHeight: 1.2 }}>
          {t('pernyataan.heading')}
        </Text>
        <Text size="sm" style={{ color: themeColors.textSecondary, marginTop: 6 }}>
          {t('pernyataan.lead')}
        </Text>
      </div>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <TextInput
          label={t('pernyataan.field.place')}
          placeholder={t('pernyataan.placeholder.place')}
          value={place}
          onChange={(event) => setPlace(event.currentTarget.value)}
          onBlur={() => setPlace((value) => value.trim().toUpperCase())}
          disabled={loading}
          styles={inputStyles}
          data-autofocus
        />
        <TextInput
          label={t('pernyataan.field.name')}
          placeholder={t('pernyataan.placeholder.name')}
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          onBlur={() => setName((value) => value.trim().toUpperCase())}
          disabled={loading}
          styles={inputStyles}
        />
      </SimpleGrid>
      <TextInput
        label={t('pernyataan.field.position')}
        placeholder={t('pernyataan.placeholder.position')}
        value={position}
        onChange={(event) => setPosition(event.currentTarget.value)}
        onBlur={() => setPosition((value) => value.trim().toUpperCase())}
        disabled={loading}
        styles={inputStyles}
      />
      <Text size="xs" style={{ color: themeColors.textSecondary, marginTop: -8 }}>
        {t('pernyataan.utamaHint')}
      </Text>

      {conflict && (
        <StatementConflictPanel
          current={conflict.current}
          onReload={() => conflict.current && adoptCurrent(conflict.current)}
          onOverwrite={() => conflict.current && save(expectedUpdatedAtFor(conflict.current))}
          overwriting={loading}
          loadFailed={conflict.loadFailed}
          onRetryLoad={() => void loadConflictRow()}
        />
      )}

      {error && (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      )}

      <Alert
        variant="light"
        color="yellow"
        icon={<IconInfoCircle size={16} />}
        styles={{ message: { color: themeColors.text } }}
      >
        <Text size="sm" fw={600} component="span" style={{ color: themeColors.text }}>
          {t('pernyataan.note.title')}
        </Text>{' '}
        <Text size="sm" component="span" style={{ color: themeColors.text }}>
          {t('pernyataan.note.body')}
        </Text>
      </Alert>
    </Stack>
  );
});

StepPernyataan.displayName = 'StepPernyataan';

export default StepPernyataan;
